import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Breadcrumb,
    Button,
    Card,
    Layout,
    Result,
    Space,
    Spin,
    Steps,
    Tag,
    Typography,
    Upload,
    message,
    theme,
} from "antd";
import {
    InboxOutlined,
    UserAddOutlined,
} from "@ant-design/icons";
import type { RcFile } from "antd/es/upload/interface";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useScopedNavigate, useAdminPath } from "../components/MarketerView/useScopedNavigate";
import { useAuth } from "../hooks/useAuth";
import {
    useCancelImportMutation,
    useCommitImportMutation,
    useGetActiveSegmentationFieldsQuery,
    useGetImportJobQuery,
    useSetImportMappingMutation,
    useUploadImportFileMutation,
} from "../services/nestjs-base-api";
import {
    ICustomerImportJob,
    ImportColumnMapping,
    ImportConflictPolicy,
    ImportMatchStrategy,
    ISegmentationField,
} from "../services/nestjs-base-api";
import { ColumnMappingWizard } from "../components/customers/ColumnMappingWizard";
import { ImportPreviewTable } from "../components/customers/ImportPreviewTable";

const { Content } = Layout;
const { Dragger } = Upload;
const { Title, Paragraph, Text } = Typography;

type WizardStep = "upload" | "mapping" | "preview" | "done";

const CustomerImportPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const adminPath = useAdminPath();
    const { user } = useAuth();
    const params = useParams<{ jobId?: string }>();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const jwtToken = user?.jwtToken ?? "";

    const [jobId, setJobId] = useState<number | null>(
        params.jobId ? Number(params.jobId) : null,
    );
    const [step, setStep] = useState<WizardStep>(jobId ? "mapping" : "upload");
    const [uploading, setUploading] = useState(false);

    const { data: job, refetch: refetchJob } = useGetImportJobQuery(
        { jwtToken, jobId: jobId ?? 0 },
        { skip: !jwtToken || !jobId, pollingInterval: 0 },
    );
    const { data: fieldsData } = useGetActiveSegmentationFieldsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );
    const fields = useMemo<ISegmentationField[]>(() => fieldsData ?? [], [fieldsData]);

    const [uploadFile] = useUploadImportFileMutation();
    const [setMapping, { isLoading: settingMapping }] = useSetImportMappingMutation();
    const [commitImport, { isLoading: committing }] = useCommitImportMutation();
    const [cancelImport] = useCancelImportMutation();

    // Track local mapping draft so the wizard is responsive while the user
    // tweaks (the API only persists when they click "Validate preview").
    const [draftMapping, setDraftMapping] = useState<ImportColumnMapping>({});
    const [draftMatchStrategy, setDraftMatchStrategy] = useState<ImportMatchStrategy>("email");
    const [draftConflictPolicy, setDraftConflictPolicy] = useState<ImportConflictPolicy>("skip");

    // When a job loads (or refreshes), seed the draft from the persisted state.
    useEffect(() => {
        if (!job) return;
        if (job.columnMapping) setDraftMapping(job.columnMapping);
        if (job.matchStrategy) setDraftMatchStrategy(job.matchStrategy);
        if (job.conflictPolicy) setDraftConflictPolicy(job.conflictPolicy);

        if (job.state === "awaiting_mapping") setStep("mapping");
        else if (job.state === "previewing") setStep("preview");
        else if (job.state === "completed") setStep("done");
        else if (job.state === "failed" || job.state === "cancelled") setStep("done");
    }, [job]);

    const handleUpload = async (file: RcFile): Promise<boolean> => {
        setUploading(true);
        try {
            const result: ICustomerImportJob = await uploadFile({ jwtToken, file: file as File }).unwrap();
            setJobId(result.id);
            navigate(`/dashboard/customers/import/${result.id}`, { replace: true });
            message.success(intl.formatMessage({
                id: "customers.import.toast.uploaded",
                defaultMessage: "Uploaded {n} row(s)",
            }, { n: result.totalRows ?? 0 }));
        } catch (err: any) {
            const detail = err?.data?.message || err?.error || "Upload failed";
            message.error(detail);
        } finally {
            setUploading(false);
        }
        return false;
    };

    const handleSubmitMapping = async () => {
        if (!jobId) return;
        try {
            await setMapping({
                jwtToken,
                jobId,
                payload: {
                    columnMapping: draftMapping,
                    matchStrategy: draftMatchStrategy,
                    conflictPolicy: draftConflictPolicy,
                },
            }).unwrap();
            await refetchJob();
            message.success(intl.formatMessage({
                id: "customers.import.toast.previewReady",
                defaultMessage: "Mapping applied — review the preview",
            }));
        } catch (err: any) {
            message.error(err?.data?.message || "Mapping failed");
        }
    };

    const handleCommit = async () => {
        if (!jobId) return;
        try {
            await commitImport({ jwtToken, jobId }).unwrap();
            await refetchJob();
            message.success(intl.formatMessage({
                id: "customers.import.toast.committed",
                defaultMessage: "Import complete",
            }));
        } catch (err: any) {
            message.error(err?.data?.message || "Commit failed");
        }
    };

    const handleCancel = async () => {
        if (!jobId) return;
        try {
            await cancelImport({ jwtToken, jobId }).unwrap();
            navigate("/dashboard/customers");
        } catch (err: any) {
            message.error(err?.data?.message || "Cancel failed");
        }
    };

    const stepIndex =
        step === "upload" ? 0 :
        step === "mapping" ? 1 :
        step === "preview" ? 2 : 3;

    return (
        <Content style={{ padding: 24 }}>
            <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                    { title: intl.formatMessage({ id: "breadcrumb.dashboard", defaultMessage: "Dashboard" }) },
                    { title: intl.formatMessage({ id: "customers.breadcrumb", defaultMessage: "Customers" }), href: adminPath("/dashboard/customers") },
                    { title: intl.formatMessage({ id: "customers.import.breadcrumb", defaultMessage: "Import" }) },
                ]}
            />
            <Card
                style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
                title={
                    <Space>
                        <UserAddOutlined />
                        <Title level={4} style={{ margin: 0 }}>
                            {intl.formatMessage({ id: "customers.import.title", defaultMessage: "Import customers" })}
                        </Title>
                    </Space>
                }
            >
                <Steps
                    current={stepIndex}
                    style={{ marginBottom: 24 }}
                    items={[
                        { title: intl.formatMessage({ id: "customers.import.step.upload", defaultMessage: "Upload" }) },
                        { title: intl.formatMessage({ id: "customers.import.step.map", defaultMessage: "Map columns" }) },
                        { title: intl.formatMessage({ id: "customers.import.step.preview", defaultMessage: "Preview" }) },
                        { title: intl.formatMessage({ id: "customers.import.step.done", defaultMessage: "Done" }) },
                    ]}
                />

                {step === "upload" && (
                    <UploadStep
                        uploading={uploading}
                        onPickFile={handleUpload}
                    />
                )}

                {step === "mapping" && job && (
                    <MappingStep
                        job={job}
                        fields={fields}
                        mapping={draftMapping}
                        matchStrategy={draftMatchStrategy}
                        conflictPolicy={draftConflictPolicy}
                        onMappingChange={setDraftMapping}
                        onMatchStrategyChange={setDraftMatchStrategy}
                        onConflictPolicyChange={setDraftConflictPolicy}
                        onCancel={handleCancel}
                        onSubmit={handleSubmitMapping}
                        submitting={settingMapping}
                    />
                )}

                {step === "preview" && job && (
                    <PreviewStep
                        job={job}
                        onBack={() => setStep("mapping")}
                        onCommit={handleCommit}
                        committing={committing}
                        onCancel={handleCancel}
                    />
                )}

                {step === "done" && job && (
                    <DoneStep job={job} onClose={() => navigate("/dashboard/customers")} />
                )}

                {!job && jobId && (
                    <div style={{ textAlign: "center", padding: 48 }}>
                        <Spin />
                    </div>
                )}
            </Card>
        </Content>
    );
};

const UploadStep: React.FC<{
    uploading: boolean;
    onPickFile: (file: RcFile) => Promise<boolean>;
}> = ({ uploading, onPickFile }) => {
    const intl = useIntl();
    return (
        <Dragger
            name="file"
            multiple={false}
            disabled={uploading}
            accept=".csv,.xlsx,.xlsm"
            beforeUpload={onPickFile}
            showUploadList={false}
        >
            <p className="ant-upload-drag-icon">
                <InboxOutlined />
            </p>
            <p className="ant-upload-text">
                {intl.formatMessage({
                    id: "customers.import.upload.cta",
                    defaultMessage: "Click or drag a CSV or Excel file to upload",
                })}
            </p>
            <p className="ant-upload-hint">
                {intl.formatMessage({
                    id: "customers.import.upload.hint",
                    defaultMessage: "Up to 50MB / 100,000 rows. The first row should be column headers.",
                })}
            </p>
            {uploading && <Spin style={{ marginTop: 12 }} />}
        </Dragger>
    );
};

const MappingStep: React.FC<{
    job: ICustomerImportJob;
    fields: ISegmentationField[];
    mapping: ImportColumnMapping;
    matchStrategy: ImportMatchStrategy;
    conflictPolicy: ImportConflictPolicy;
    onMappingChange: (m: ImportColumnMapping) => void;
    onMatchStrategyChange: (s: ImportMatchStrategy) => void;
    onConflictPolicyChange: (p: ImportConflictPolicy) => void;
    onCancel: () => void;
    onSubmit: () => void;
    submitting: boolean;
}> = (props) => {
    const intl = useIntl();
    return (
        <>
            <Paragraph type="secondary">
                {intl.formatMessage({
                    id: "customers.import.mapping.intro",
                    defaultMessage: "Match each source column to a customer field. We auto-suggest where the names look similar.",
                })}{" "}
                <Text strong>
                    {intl.formatMessage(
                        { id: "customers.import.mapping.fileSummary", defaultMessage: "{file} — {rows} row(s)" },
                        { file: props.job.fileName, rows: props.job.totalRows ?? 0 },
                    )}
                </Text>
            </Paragraph>
            <ColumnMappingWizard
                headers={props.job.headers ?? []}
                fields={props.fields}
                mapping={props.mapping}
                matchStrategy={props.matchStrategy}
                conflictPolicy={props.conflictPolicy}
                onMappingChange={props.onMappingChange}
                onMatchStrategyChange={props.onMatchStrategyChange}
                onConflictPolicyChange={props.onConflictPolicyChange}
            />
            <div style={{ marginTop: 24, textAlign: "right" }}>
                <Space>
                    <Button onClick={props.onCancel}>
                        {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                    </Button>
                    <Button type="primary" onClick={props.onSubmit} loading={props.submitting}>
                        {intl.formatMessage({
                            id: "customers.import.mapping.cta",
                            defaultMessage: "Validate and preview",
                        })}
                    </Button>
                </Space>
            </div>
        </>
    );
};

const PreviewStep: React.FC<{
    job: ICustomerImportJob;
    onBack: () => void;
    onCommit: () => void;
    committing: boolean;
    onCancel: () => void;
}> = ({ job, onBack, onCommit, committing, onCancel }) => {
    const intl = useIntl();
    const total = job.totalRows ?? 0;
    const valid = job.validRows ?? 0;
    const invalid = job.errorRows ?? 0;

    return (
        <>
            <Space style={{ marginBottom: 16 }} wrap>
                <Tag color="blue">
                    {intl.formatMessage({ id: "customers.import.preview.total", defaultMessage: "Total: {n}" }, { n: total })}
                </Tag>
                <Tag color="green">
                    {intl.formatMessage({ id: "customers.import.preview.valid", defaultMessage: "Valid: {n}" }, { n: valid })}
                </Tag>
                <Tag color={invalid > 0 ? "red" : "default"}>
                    {intl.formatMessage({ id: "customers.import.preview.invalid", defaultMessage: "Invalid: {n}" }, { n: invalid })}
                </Tag>
            </Space>

            {invalid > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={intl.formatMessage({
                        id: "customers.import.preview.someInvalid.title",
                        defaultMessage: "Some rows have errors and will be skipped",
                    })}
                    description={intl.formatMessage({
                        id: "customers.import.preview.someInvalid.body",
                        defaultMessage: "Only valid rows will be committed. Use the Errors tab below to see what went wrong.",
                    })}
                />
            )}

            <ImportPreviewTable jobId={job.id} />

            <div style={{ marginTop: 24, textAlign: "right" }}>
                <Space>
                    <Button onClick={onCancel} danger>
                        {intl.formatMessage({ id: "customers.import.cancel", defaultMessage: "Cancel import" })}
                    </Button>
                    <Button onClick={onBack}>
                        {intl.formatMessage({ id: "customers.import.preview.back", defaultMessage: "Back to mapping" })}
                    </Button>
                    <Button
                        type="primary"
                        onClick={onCommit}
                        loading={committing}
                        disabled={valid === 0}
                    >
                        {intl.formatMessage(
                            { id: "customers.import.preview.commit", defaultMessage: "Commit {n} row(s)" },
                            { n: valid },
                        )}
                    </Button>
                </Space>
            </div>
        </>
    );
};

const DoneStep: React.FC<{ job: ICustomerImportJob; onClose: () => void }> = ({ job, onClose }) => {
    const intl = useIntl();
    if (job.state === "completed") {
        return (
            <Result
                status="success"
                title={intl.formatMessage({ id: "customers.import.done.title", defaultMessage: "Import complete" })}
                subTitle={intl.formatMessage(
                    {
                        id: "customers.import.done.body",
                        defaultMessage: "{valid} customer(s) imported, {invalid} row(s) skipped due to errors.",
                    },
                    { valid: job.validRows ?? 0, invalid: job.errorRows ?? 0 },
                )}
                extra={[
                    <Button key="back" type="primary" onClick={onClose}>
                        {intl.formatMessage({ id: "customers.import.done.cta", defaultMessage: "Back to customers" })}
                    </Button>,
                ]}
            />
        );
    }
    if (job.state === "cancelled") {
        return (
            <Result
                status="info"
                title={intl.formatMessage({ id: "customers.import.cancelled.title", defaultMessage: "Import cancelled" })}
                extra={[
                    <Button key="back" type="primary" onClick={onClose}>
                        {intl.formatMessage({ id: "customers.import.done.cta", defaultMessage: "Back to customers" })}
                    </Button>,
                ]}
            />
        );
    }
    return (
        <Result
            status="error"
            title={intl.formatMessage({ id: "customers.import.failed.title", defaultMessage: "Import failed" })}
            subTitle={job.errorMessage ?? ""}
            extra={[
                <Button key="back" type="primary" onClick={onClose}>
                    {intl.formatMessage({ id: "customers.import.done.cta", defaultMessage: "Back to customers" })}
                </Button>,
            ]}
        />
    );
};

export default CustomerImportPage;
